import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { auth } from '../../../firebase/config';

const CLOUDINARY_URL = 'https://api.cloudinary.com/v1_1/dn9cspqmi/image/upload';
const UPLOAD_PRESET = 'ml_default';

export interface GuardDocument {
  id: string;
  name: string;
  url: string;
  publicId?: string;
  deleteToken?: string;
  uploadedAt: Date;
  uploadedBy: string;
  fileType?: string;
  fileSize?: number;
}

export interface GuardWithDocuments {
  id: string;
  name: string;
  guardId: string;
  cnic: string;
  image?: string;
  slotNumber?: number;
  documents?: GuardDocument[];
}

/**
 * Upload document to Cloudinary
 */
/**
 * Upload document to Cloudinary
 */
export const uploadDocument = async (file: File): Promise<{ url: string; publicId: string; deleteToken: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', UPLOAD_PRESET);

  const response = await fetch(CLOUDINARY_URL, {
    method: 'POST',
    body: formData,
  });

  const data = await response.json();
  if (data.secure_url) {
    return {
      url: data.secure_url,
      publicId: data.public_id,
      deleteToken: data.delete_token
    };
  }
  throw new Error('Document upload failed');
};

/**
 * Delete document from Cloudinary using delete token
 */
export const deleteFromCloudinary = async (deleteToken: string): Promise<boolean> => {
  if (!deleteToken) return false;

  const formData = new FormData();
  formData.append('token', deleteToken);

  try {
    const response = await fetch('https://api.cloudinary.com/v1_1/dn9cspqmi/delete_by_token', {
      method: 'POST',
      body: formData,
    });
    const data = await response.json();
    return data.result === 'ok';
  } catch (error) {
    console.error('Error deleting from Cloudinary:', error);
    return false;
  }
};

/**
 * Add document to guard in client's current deployment
 */
export const addGuardDocument = async (
  clientPath: string,
  guardId: string,
  file: File,
  documentName: string
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  // Upload document to Cloudinary
  const documentUrl = await uploadDocument(file);

  // Get client document
  const clientRef = doc(db, clientPath);
  const clientSnap = await getDoc(clientRef);

  if (!clientSnap.exists()) {
    throw new Error('Client document not found');
  }

  const clientData = clientSnap.data();
  const currentDeployment = clientData?.currentDeployment || {};
  const guards = Array.isArray(currentDeployment.guard)
    ? currentDeployment.guard
    : currentDeployment.guard
      ? [currentDeployment.guard]
      : [];

  // Find the guard and add document
  const updatedGuards = guards.map((guard: any) => {
    if (guard.id === guardId || guard.guardId === guardId) {
      const documents = guard.documents || [];
      const newDocument: GuardDocument = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: documentName || file.name,
        url: documentUrl.url,
        publicId: documentUrl.publicId,
        deleteToken: documentUrl.deleteToken,
        uploadedAt: new Date(),
        uploadedBy: user.uid,
        fileType: file.type,
        fileSize: file.size,
      };
      return {
        ...guard,
        documents: [...documents, newDocument],
      };
    }
    return guard;
  });

  // Update client document
  await updateDoc(clientRef, {
    'currentDeployment.guard': updatedGuards,
    lastDeploymentUpdate: serverTimestamp(),
  });
};

/**
 * Delete document from guard in client's current deployment
 */
export const deleteGuardDocument = async (
  clientPath: string,
  guardId: string,
  documentId: string
): Promise<void> => {
  // Get client document
  const clientRef = doc(db, clientPath);
  const clientSnap = await getDoc(clientRef);

  if (!clientSnap.exists()) {
    throw new Error('Client document not found');
  }

  const clientData = clientSnap.data();
  const currentDeployment = clientData?.currentDeployment || {};
  const guards = Array.isArray(currentDeployment.guard)
    ? currentDeployment.guard
    : currentDeployment.guard
      ? [currentDeployment.guard]
      : [];

  // Find the guard and remove document
  // Find the guard and remove document
  const updatedGuards = await Promise.all(guards.map(async (guard: any) => {
    if (guard.id === guardId || guard.guardId === guardId) {
      const documents = guard.documents || [];
      const docToDelete = documents.find((doc: GuardDocument) => doc.id === documentId);

      if (docToDelete && docToDelete.deleteToken) {
        await deleteFromCloudinary(docToDelete.deleteToken);
      }

      const updatedDocuments = documents.filter(
        (doc: GuardDocument) => doc.id !== documentId
      );
      return {
        ...guard,
        documents: updatedDocuments,
      };
    }
    return guard;
  }));

  // Update client document
  await updateDoc(clientRef, {
    'currentDeployment.guard': updatedGuards,
    lastDeploymentUpdate: serverTimestamp(),
  });
};

/**
 * Get guard documents from client's current deployment
 */
export const getGuardDocuments = async (
  clientPath: string,
  guardId: string
): Promise<GuardDocument[]> => {
  const clientRef = doc(db, clientPath);
  const clientSnap = await getDoc(clientRef);

  if (!clientSnap.exists()) {
    return [];
  }

  const clientData = clientSnap.data();
  const currentDeployment = clientData?.currentDeployment || {};
  const guards = Array.isArray(currentDeployment.guard)
    ? currentDeployment.guard
    : currentDeployment.guard
      ? [currentDeployment.guard]
      : [];

  const guard = guards.find(
    (g: any) => g.id === guardId || g.guardId === guardId
  );

  if (!guard) {
    return [];
  }

  // Convert Firestore Timestamps to Date objects
  return (guard.documents || []).map((doc: any) => {
    let uploadedAt = doc.uploadedAt;
    if (uploadedAt && typeof uploadedAt === 'object') {
      if (typeof uploadedAt.toDate === 'function') {
        uploadedAt = uploadedAt.toDate();
      } else if (uploadedAt.seconds || uploadedAt._seconds) {
        const seconds = uploadedAt.seconds || uploadedAt._seconds || 0;
        const nanoseconds = uploadedAt.nanoseconds || uploadedAt._nanoseconds || 0;
        uploadedAt = new Date(seconds * 1000 + nanoseconds / 1000000);
      } else if (typeof uploadedAt === 'string') {
        uploadedAt = new Date(uploadedAt);
      }
    }
    return {
      ...doc,
      uploadedAt,
    };
  });
};

/**
 * Get all guards with their documents from client's current deployment
 */
export const getGuardsWithDocuments = async (
  clientPath: string
): Promise<GuardWithDocuments[]> => {
  const clientRef = doc(db, clientPath);
  const clientSnap = await getDoc(clientRef);

  if (!clientSnap.exists()) {
    return [];
  }

  const clientData = clientSnap.data();
  const currentDeployment = clientData?.currentDeployment || {};
  const guards = Array.isArray(currentDeployment.guard)
    ? currentDeployment.guard
    : currentDeployment.guard
      ? [currentDeployment.guard]
      : [];

  return guards.map((guard: any) => ({
    id: guard.id,
    name: guard.name,
    guardId: guard.guardId,
    cnic: guard.cnic,
    image: guard.image,
    slotNumber: guard.slotNumber,
    documents: (guard.documents || []).map((doc: any) => {
      // Convert Firestore Timestamp to Date if needed
      let uploadedAt = doc.uploadedAt;
      if (uploadedAt && typeof uploadedAt === 'object') {
        if (typeof uploadedAt.toDate === 'function') {
          uploadedAt = uploadedAt.toDate();
        } else if (uploadedAt.seconds || uploadedAt._seconds) {
          const seconds = uploadedAt.seconds || uploadedAt._seconds || 0;
          const nanoseconds = uploadedAt.nanoseconds || uploadedAt._nanoseconds || 0;
          uploadedAt = new Date(seconds * 1000 + nanoseconds / 1000000);
        } else if (typeof uploadedAt === 'string') {
          uploadedAt = new Date(uploadedAt);
        }
      }
      return {
        ...doc,
        uploadedAt,
      };
    }),
  }));
};

/**
 * Check if all guards have documents
 */
export const checkAllGuardsHaveDocuments = async (
  clientPath: string
): Promise<boolean> => {
  const guards = await getGuardsWithDocuments(clientPath);

  if (guards.length === 0) {
    return false;
  }

  return guards.every((guard) => guard.documents && guard.documents.length > 0);
};

/**
 * Remove documents from guard when moved to history
 * This is called when a guard is removed from current deployment
 */
export const removeGuardDocumentsFromClient = async (
  clientPath: string,
  guardId: string
): Promise<void> => {
  const clientRef = doc(db, clientPath);
  const clientSnap = await getDoc(clientRef);

  if (!clientSnap.exists()) {
    return;
  }

  const clientData = clientSnap.data();
  const currentDeployment = clientData?.currentDeployment || {};
  const guards = Array.isArray(currentDeployment.guard)
    ? currentDeployment.guard
    : currentDeployment.guard
      ? [currentDeployment.guard]
      : [];

  // Remove documents from the guard before moving to history
  const updatedGuards = guards.map((guard: any) => {
    if (guard.id === guardId || guard.guardId === guardId) {
      const { documents, ...guardWithoutDocuments } = guard;
      return guardWithoutDocuments;
    }
    return guard;
  });

  // Update client document
  await updateDoc(clientRef, {
    'currentDeployment.guard': updatedGuards,
    lastDeploymentUpdate: serverTimestamp(),
  });
};

